import React, { useMemo } from 'react';
import { TableModal } from './Modal';

// City Distribution data type
export interface CityDistributionData {
    city: string;
    totalVehicle: number;
    percentageTotal: number;
    growthMoM: number; // Now available for use
    top3Clients: string;
}

// City Distribution Modal Props
interface CityDistributionModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: CityDistributionData[];
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

// Sort field enum for type safety
enum CityDistributionSortField {
    CITY = 'city',
    TOTAL_VEHICLE = 'totalVehicle',
    PERCENTAGE = 'percentageTotal',
    GROWTH = 'growthMoM', // Now enabled
    CLIENTS = 'top3Clients'
}

// Column configuration
const COLUMN_CONFIG = {
    [CityDistributionSortField.CITY]: {
        title: 'City',
        sortable: true,
        render: (value: string) => (
            <span className="font-jakarta font-medium text-neutral-900">
                {value || 'Unknown City'}
            </span>
        )
    },
    [CityDistributionSortField.TOTAL_VEHICLE]: {
        title: 'Total Vehicle',
        sortable: true,
        render: (value: number) => (
            <span className="font-jakarta font-normal text-neutral-900 tabular-nums">
                {(value ?? 0).toLocaleString('en-US')}
            </span>
        )
    },
    [CityDistributionSortField.PERCENTAGE]: {
        title: '% of Total Vehicle',
        sortable: true,
        render: (value: number) => (
            <span 
                className="font-jakarta font-normal tabular-nums"
                style={{ color: '#5C5C5C' }}
            >
                {(value ?? 0).toFixed(1)}%
            </span>
        )
    },

    [CityDistributionSortField.GROWTH]: {
        title: 'Growth MoM',
        sortable: true,
        render: (value: number) => (
            <div className="flex items-center gap-2">
                <div
                    className="w-[32px] h-[32px] rounded-full flex items-center justify-center"
                    style={{ border: '1px solid #EBEBEB' }}
                >
                    {value >= 0 ? (
                        <i className="ri-arrow-up-line text-green-500 text-lg"></i>
                    ) : (
                        <i className="ri-arrow-down-line text-red-500 text-lg"></i>
                    )}
                </div>
                <span
                    className={`font-jakarta font-medium tabular-nums text-sm leading-5 tracking-tight ${value >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                    style={{ letterSpacing: '-0.006em' }}
                >
                    {value >= 0 ? '+' : ''}{value}%
                </span>
            </div>
        )
    },

    [CityDistributionSortField.CLIENTS]: {
        title: 'Top 3 Clients',
        sortable: true,
        render: (value: string) => (
            <span 
                className="font-jakarta font-normal"
                style={{ color: '#5C5C5C' }}
            >
                {value || 'N/A'}
            </span>
        )
    }
};

// Column order for consistent display and CSV export
const COLUMN_ORDER = [
    CityDistributionSortField.CITY,
    CityDistributionSortField.TOTAL_VEHICLE,
    CityDistributionSortField.PERCENTAGE,
    CityDistributionSortField.GROWTH, // Now enabled
    CityDistributionSortField.CLIENTS
];

const CityDistributionModal: React.FC<CityDistributionModalProps> = ({
    isOpen,
    onClose,
    data,
    loading = false,
    hasMore = false,
    onLoadMore
}) => {
    // Generate columns from configuration
    const columns = useMemo(() => {
        return COLUMN_ORDER.map(field => ({
            key: field,
            title: COLUMN_CONFIG[field].title,
            sortable: COLUMN_CONFIG[field].sortable,
            render: COLUMN_CONFIG[field].render
        }));
    }, []);

    // Sort function
    const handleSort = (data: CityDistributionData[], field: string, direction: 'asc' | 'desc') => {
        return [...data].sort((a, b) => {
            let aValue = a[field as keyof CityDistributionData];
            let bValue = b[field as keyof CityDistributionData];

            // Handle different data types
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
            }

            if (aValue < bValue) {
                return direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Get column order for CSV export
    const getColumnOrder = () => COLUMN_ORDER;

    // Get CSV value for a specific field
    const getCsvValue = (row: CityDistributionData, field: string): string | number => {
        switch (field as CityDistributionSortField) {
            case CityDistributionSortField.CITY:
                return row.city;
            case CityDistributionSortField.TOTAL_VEHICLE:
                return row.totalVehicle;
            case CityDistributionSortField.PERCENTAGE:
                return `${row.percentageTotal}%`;
            case CityDistributionSortField.GROWTH:
                return `${row.growthMoM >= 0 ? '+' : ''}${row.growthMoM}%`;
            case CityDistributionSortField.CLIENTS:
                return row.top3Clients;
            default:
                return '';
        }
    };

    return (
        <TableModal<CityDistributionData>
            isOpen={isOpen}
            onClose={onClose}
            title="City Distribution Detail"
            modalTitle="City Distribution"
            description="Geographic distribution and monthly growth of the vehicle fleet."
            data={data}
            columns={columns}
            sortConfig={{
                defaultField: CityDistributionSortField.TOTAL_VEHICLE,
                defaultDirection: 'desc'
            }}
            csvFileName="city-distribution.csv"
            onSort={handleSort}
            getColumnOrder={getColumnOrder}
            getCsvValue={getCsvValue}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
        />
    );
};

export default CityDistributionModal;